// MediaGallery — read-only thumbnail strip that opens a full-screen lightbox
// on click, with per-image captions. Shared by every viewer-facing surface
// (Timeline feed drawers, FeatureDetail read view) and reused for the preview
// popups in the editor's MediaUpload. Captions come from a URL→text map so a
// caption survives reorder/removal of other images.
import { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { DialogOverlay, DialogPortal } from "@/ui/dialog";
import { cn } from "@/lib/cn";

export const VIDEO_RE = /\.(mp4|webm|mov|m4v)(\?|$)/i;

export interface MediaItem {
  url: string;
  caption?: string;
}

/** Zip an ordered URL list with its caption map into renderable items. */
export function toMediaItems(
  urls: string[],
  captions?: Record<string, string> | null,
): MediaItem[] {
  return urls.map((url) => ({ url, caption: captions?.[url] || undefined }));
}

/** Full-screen media viewer. `index` is the active item (null = closed);
 *  `onIndex` moves it or closes (null). Arrow keys page; Esc / backdrop click
 *  close (Radix Dialog handles Esc, focus trap, and scroll lock). */
export function ImageLightbox({
  items,
  index,
  onIndex,
}: {
  items: MediaItem[];
  index: number | null;
  onIndex: (i: number | null) => void;
}) {
  const open = index !== null;
  const count = items.length;
  const active = open ? items[index] : undefined;
  const go = (delta: number) => {
    if (index === null || count === 0) return;
    onIndex((index + delta + count) % count);
  };
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onIndex(null)}>
      <DialogPortal>
        <DialogOverlay className="z-50 bg-black/80" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") {
              e.preventDefault();
              go(1);
            } else if (e.key === "ArrowLeft") {
              e.preventDefault();
              go(-1);
            }
          }}
          // Clicking the empty backdrop (the content container itself, not a
          // child) closes; children stop that by being a different target.
          onClick={(e) => {
            if (e.target === e.currentTarget) onIndex(null);
          }}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center p-4 focus:outline-none sm:p-10"
        >
          <DialogPrimitive.Title className="sr-only">
            Media preview
          </DialogPrimitive.Title>

          <DialogPrimitive.Close
            aria-label="Close"
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-xl text-white/90 transition-colors hover:bg-white/20"
          >
            ×
          </DialogPrimitive.Close>

          {count > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous"
                onClick={() => go(-1)}
                className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-2xl text-white/90 transition-colors hover:bg-white/20"
              >
                ‹
              </button>
              <button
                type="button"
                aria-label="Next"
                onClick={() => go(1)}
                className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-2xl text-white/90 transition-colors hover:bg-white/20"
              >
                ›
              </button>
            </>
          )}

          {active &&
            (VIDEO_RE.test(active.url) ? (
              <video
                src={active.url}
                controls
                autoPlay
                playsInline
                className="max-h-[82vh] max-w-full rounded-md"
              />
            ) : (
              <img
                src={active.url}
                alt={active.caption ?? ""}
                className="max-h-[82vh] max-w-full rounded-md object-contain"
              />
            ))}

          {active?.caption && (
            <p className="mt-4 max-w-2xl text-center text-sm leading-relaxed text-white/85">
              {active.caption}
            </p>
          )}
          {count > 1 && (
            <div className="mt-2 text-xs tabular-nums text-white/55">
              {index! + 1} / {count}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
}

/** Thumbnail grid + lightbox for viewing (no upload/edit). Renders nothing
 *  when there are no images.
 *
 *  variant="compact" is the on-card form for the Timeline: smaller tiles, no
 *  inline captions (they still show in the lightbox), and a "+N" badge on the
 *  last tile when the image count exceeds `max`. Thumbnail clicks stop
 *  propagation so opening the lightbox from inside a clickable card doesn't
 *  also trigger the card's own click (e.g. opening its detail drawer). */
export function MediaGallery({
  urls,
  captions,
  className,
  variant = "default",
  max,
}: {
  urls: string[] | null | undefined;
  captions?: Record<string, string> | null;
  className?: string;
  variant?: "default" | "compact";
  max?: number;
}) {
  const items = toMediaItems(urls ?? [], captions);
  const [index, setIndex] = useState<number | null>(null);
  if (items.length === 0) return null;

  const compact = variant === "compact";
  const limit = max ?? (compact ? 4 : items.length);
  const shown = items.slice(0, limit);
  const overflow = items.length - shown.length;

  return (
    <div className={className}>
      <div className={cn("flex flex-wrap", compact ? "gap-2" : "gap-3")}>
        {shown.map((it, i) => {
          const showOverflow = compact && overflow > 0 && i === shown.length - 1;
          return (
            <figure key={it.url} className={compact ? undefined : "w-32"}>
              <button
                type="button"
                // Stop propagation so a thumbnail click inside a clickable card
                // opens the lightbox without also opening the card's drawer.
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex(i);
                }}
                className={cn(
                  "group relative block overflow-hidden rounded-md border border-border bg-bg transition-colors hover:border-border-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50",
                  compact ? "h-16 w-24" : "h-24 w-32",
                )}
                title={it.caption || "Open"}
              >
                {VIDEO_RE.test(it.url) ? (
                  <video
                    src={it.url}
                    muted
                    playsInline
                    preload="metadata"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <img
                    src={it.url}
                    alt={it.caption ?? ""}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                )}
                {VIDEO_RE.test(it.url) && !showOverflow && (
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-2xl text-white/90 drop-shadow">
                    ▶
                  </span>
                )}
                {showOverflow && (
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/55 text-sm font-semibold text-white">
                    +{overflow}
                  </span>
                )}
              </button>
              {!compact && it.caption && (
                <figcaption className="mt-1 line-clamp-2 text-[11px] leading-snug text-text-tertiary">
                  {it.caption}
                </figcaption>
              )}
            </figure>
          );
        })}
      </div>
      <ImageLightbox items={items} index={index} onIndex={setIndex} />
    </div>
  );
}
